import classNames from 'classnames';

import CardContent from './CardContent';
import { CardGridFragmentFragment } from '../../__generated__/graphql';
import Card, { CardBorder } from '../../components/Card';
import GradientBorderCard from '../../components/GradientBorderCard';
import { Paragraph } from '../../components/Text';
import Section from '../../layout/Section';
import SectionTitle from '../../layout/Section/SectionTitle';
import { ContentBlock } from '../types';

import style from './card-grid.module.css';

type CardGridMaxColumns = 2 | 3 | 4;

/* eslint-disable @typescript-eslint/naming-convention */
const maxColumnsMap: Record<CardGridMaxColumns, string> & Record<number, string> = {
  2: style.cardGridColumns2,
  3: style.cardGridColumns3,
  4: style.cardGridColumns4,
};

const cardBorderMap: Record<string, CardBorder> = {
  Black: 'black',
  None: 'none',
  Turquoise: 'turquoise',
};
/* eslint-enable @typescript-eslint/naming-convention */

const CardGrid: ContentBlock<CardGridFragmentFragment> = ({ fragment }) => {
  const contentBlockProps = fragment.cardGrid;

  if (!contentBlockProps) {
    return null;
  }

  const blackSectionBackground = contentBlockProps.sectionBackgroundColor === 'Black';
  const blackCardBackground = contentBlockProps.cardBackgroundColor === 'Black';

  const maxElementCount = contentBlockProps.cards?.reduce(
    (acc, item) => Math.max(acc, item?.card?.length ?? 0),
    0,
  ) ?? 0;

  const hasCardHeading = contentBlockProps.cards?.some(item =>
    item?.card?.some(cardContent => cardContent?.elementType?.[0] === 'Heading'));

  return (
    <Section
      backgroundColor={blackSectionBackground ? 'black' : 'white'}
      id={contentBlockProps.sectionId}
      innerContainerClassName={style.cardGridContainer}
    >
      {(!!contentBlockProps.sectionTitle || !!contentBlockProps.description) && (
        <div className={style.cardGridTextContainer}>
          {contentBlockProps.sectionTitle && (
            <SectionTitle className={style.cardGridTitle}>
              {contentBlockProps.sectionTitle}
            </SectionTitle>
          )}

          {!!(contentBlockProps.description ?? '').trim() && (
            <Paragraph
              className={style.cardGridDescription}
              fontSize={15}
            >
              {contentBlockProps.description}
            </Paragraph>
          )}
        </div>
      )}

      <div
        className={classNames(
          style.cardGrid,
          contentBlockProps.maxColumns ? maxColumnsMap[contentBlockProps.maxColumns] : undefined,
        )}
      >
        {contentBlockProps.cards?.map((item, index) => {
          if (item) {
            if (contentBlockProps.cardBorder?.toLowerCase() === 'gradient') {
              return (
                <GradientBorderCard
                  backgroundColor={blackCardBackground ? 'black' : 'white'}
                  borderWidth={1}
                  className={style.card}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                >
                  {item.card?.map((cardContent, cardIndex) => {
                    if (cardContent) {
                      return (
                        <CardContent
                          cardContent={cardContent}
                          hasCardHeading={!!hasCardHeading}
                          isDarkCardBackground={blackCardBackground}
                          // eslint-disable-next-line react/no-array-index-key
                          key={cardIndex}
                        />
                      );
                    }
                  })}
                </GradientBorderCard>
              );
            }

            return (
              <Card
                backgroundColor={blackCardBackground ? 'black' : 'white'}
                border={contentBlockProps.cardBorder ? (cardBorderMap[contentBlockProps.cardBorder] ?? 'none') : 'none'}
                className={style.card}
                elementCount={maxElementCount}
                // eslint-disable-next-line react/no-array-index-key
                key={index}
              >
                {item.card?.map((cardContent, cardIndex) => {
                  if (cardContent) {
                    return (
                      <CardContent
                        cardContent={cardContent}
                        hasCardHeading={!!hasCardHeading}
                        isDarkCardBackground={blackCardBackground}
                        // eslint-disable-next-line react/no-array-index-key
                        key={cardIndex}
                      />
                    );
                  }
                })}
              </Card>
            );
          }
        })}
      </div>
    </Section>
  );
};

export default CardGrid;
